import { useState } from "react";
import { Link } from "react-router-dom";
import { Heart } from "lucide-react";
import { useStore, formatINR } from "@/store/StoreContext";
import { imgW, srcSet, imgFallback, imgT } from "@/utils/img";
import { toast } from "sonner";

const BADGE_STYLES = {
  new: "bg-[#111111] text-[#FFFFFF]",
  bestseller: "bg-[#C59B27] text-[#111111]",
  sale: "bg-[#8B1E21] text-white",
  trending: "bg-[#1A3C34] text-white",
  handcrafted: "bg-[#FAFAFA] text-[#111111] border border-[#A8821B]",
  exclusive: "bg-[#111111] text-[#C59B27]",
  limited: "bg-[#8B1E21] text-white",
};

export default function ProductCard({ product, index = 0 }) {
  const { toggleWishlist, wishlistIds, addToCart } = useStore();
  const [sizeOpen, setSizeOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const imgs = product.images || [];
  const primary = imgs[0]?.url;
  const secondary = imgs[1]?.url;
  const discount = product.mrp > product.price ? Math.round((1 - product.price / product.mrp) * 100) : 0;
  const wished = wishlistIds.includes(product.id);
  const badges = [...(product.badges || [])];
  if (discount >= 20 && !badges.includes("sale")) badges.push("sale");
  const badge = badges[0];
  const sizeMap = {};
  const sizeVariant = {};
  (product.variants || []).forEach((v) => {
    if (v.enabled === false) return;
    if (!(v.size in sizeMap)) sizeMap[v.size] = false;
    if ((v.stock || 0) > 0) { sizeMap[v.size] = true; if (!sizeVariant[v.size]) sizeVariant[v.size] = v; }
  });
  const cardSizes = Object.keys(sizeMap).sort((a, b) => Number(a) - Number(b) || a.localeCompare(b));

  const quickAdd = async (size) => {
    const v = sizeVariant[size];
    if (!v || adding) return;
    setAdding(true);
    try { await addToCart(product.id, v.id, 1); }
    catch { toast.error("Could not add to bag — please try again"); }
    finally { setAdding(false); }
  };

  return (
    <div data-testid={`product-card-${product.slug}`}
      className="group relative bg-white border border-[#EAEAEA]/70 hover:border-[#C59B27]/60 transition-colors duration-300 tq-fade-up"
      style={{ animationDelay: `${Math.min(index, 8) * 60}ms` }}>
      <div onClick={() => cardSizes.length && setSizeOpen(true)} role="button" aria-label={`Quick shop ${product.name}`}
        className="block relative overflow-hidden aspect-[3/4] bg-[#FAFAFA] cursor-pointer">
        {primary && <img src={imgW(primary, 700)} srcSet={srcSet(primary)} sizes="(max-width:640px) 50vw, 25vw" alt={imgs[0]?.alt || product.name} loading="lazy" onError={imgFallback} style={imgT(imgs[0]?.transform)}
          className="absolute inset-0 w-full h-full transition-[opacity,transform] duration-700 ease-out group-hover:opacity-0 group-hover:scale-105" />}
        {secondary && <img src={imgW(secondary, 700)} srcSet={srcSet(secondary)} sizes="(max-width:640px) 50vw, 25vw" alt={product.name} loading="lazy" onError={imgFallback} style={imgT(imgs[1]?.transform)}
          className="absolute inset-0 w-full h-full opacity-0 transition-[opacity,transform] duration-700 ease-out group-hover:opacity-100 group-hover:scale-105" />}
        {badge && (
          <span className={`absolute top-3 left-3 text-[9px] font-bold uppercase tracking-[0.15em] px-2 py-1 ${BADGE_STYLES[badge] || BADGE_STYLES.new}`} data-testid={`badge-${product.slug}`}>{badge.replace("-", " ")}</span>
        )}
        {sizeOpen && (
          <div className="absolute inset-x-0 bottom-0 z-10 bg-white/95 backdrop-blur-sm border-t border-[#EAEAEA] p-3" data-testid={`size-picker-${product.slug}`}
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#666666]">Select Size (UK/IND)</p>
              <button onClick={() => setSizeOpen(false)} aria-label="Close sizes" data-testid={`size-picker-close-${product.slug}`} className="text-[#666666] text-sm leading-none px-1">×</button>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {cardSizes.map((s) => (
                <button key={s} disabled={!sizeMap[s] || adding} onClick={() => quickAdd(s)}
                  data-testid={`quick-size-${product.slug}-${s}`}
                  className={`min-w-[2.3rem] px-1.5 py-1.5 text-[11px] border transition-colors ${sizeMap[s] ? "border-[#111111] hover:bg-[#111111] hover:text-[#C59B27]" : "border-[#EAEAEA] text-[#c9c2b5] line-through cursor-not-allowed"}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      <button data-testid={`wishlist-toggle-${product.slug}`} onClick={() => toggleWishlist(product.id)}
        aria-label="Toggle wishlist"
        className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/90 backdrop-blur flex items-center justify-center hover:bg-[#111111] hover:text-[#C59B27] transition-colors z-10">
        <Heart size={16} className={wished ? "fill-[#8B1E21] text-[#8B1E21]" : ""} />
      </button>
      <div className="p-4 pt-3.5">
        <Link to={`/products/${product.slug}`}>
          <h3 className="font-serif text-lg leading-snug text-[#111111] line-clamp-2 min-h-[3rem] transition-colors">{product.name}</h3>
        </Link>
        {product.rating_count > 0 && (
          <p className="text-[11px] text-[#8A6A12] mt-1">★ {product.rating_avg} ({product.rating_count})</p>
        )}
        <div className="mt-2 flex items-baseline gap-2 flex-wrap">
          <span className="text-base font-semibold tracking-wide">{formatINR(product.price)}</span>
          {product.mrp > product.price && (
            <>
              <span className="text-sm text-[#8E8E93] line-through">{formatINR(product.mrp)}</span>
              <span className="text-[11px] text-[#8A6A12]">{discount}% off</span>
            </>
          )}
        </div>
        {product.color && <p className="text-[11px] text-[#8E8E93] mt-1 uppercase tracking-wider">{product.color}</p>}
        {cardSizes.length > 0 && (
          <button onClick={() => setSizeOpen(!sizeOpen)} data-testid={`select-size-${product.slug}`}
            className="mt-3 w-full border border-[#111111]/15 py-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-[#111111] hover:bg-[#111111] hover:text-white transition-colors">
            {adding ? "Adding…" : sizeOpen ? "Hide Sizes" : "Select Size"}
          </button>
        )}
      </div>
    </div>
  );
}
